/** @shell standard @header bar */
import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { View, Text, StyleSheet, RefreshControl, Platform, type TextStyle } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, withTiming, withDelay } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../components/common/Clickable';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll, HeaderIconButton } from '../components/common/ScreenHeader';
import { ContentCard } from '../components/common/ContentCard';
import { useNavigation } from '@react-navigation/native';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  DASH_STUDIO_RADIUS,
  studioCardShell,
  androidScaleFont,
  androidScaleSize,
} from '../shared/theme/studio/stackScreenShell';
import { useTranslation } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useNotifications, useMarkAllAsRead, useMarkAsRead, type NotificationStub } from '../data/domains/notifications';
import { rawNotificationIsRead } from '../data/domains/notifications';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import moment from 'moment';

const ENTER_SPRING = { stiffness: 180, damping: 22 };
const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

type FilterKey = 'all' | 'unread' | 'read';

type NotificationsStyles = ReturnType<typeof createNotificationsStyles>;

function getIconColor(type: string, c: ThemeColors) {
  switch (type) {
    case 'order':
      return c.primary;
    case 'subscription':
      return c.secondary;
    case 'promotion':
      return c.warning;
    case 'reminder':
      return c.alert;
    default:
      return c.textSecondary;
  }
}

function getIconName(type: string) {
  switch (type) {
    case 'order':
      return 'receipt';
    case 'subscription':
      return 'menu';
    case 'promotion':
      return 'tag';
    case 'reminder':
      return 'time';
    default:
      return 'bell';
  }
}

function createNotificationsStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;

  return StyleSheet.create({
    contentWrap: { flex: 1 },
    filterRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.sm,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      marginTop: appTheme.spacing.sm,
      marginBottom: STACK_SECTION_GAP,
    },
    filterPillWrap: {
      flex: 1,
      minWidth: 0,
    },
    filterPill: {
      flex: 1,
      minHeight: ss(40),
      paddingVertical: ss(10),
      paddingHorizontal: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(c.text, 0.04),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.border, 0.35),
    },
    filterPillActive: {
      backgroundColor: c.primaryUltra ?? hexToRgba(c.primary, 0.12),
      borderColor: hexToRgba(c.primary, 0.28),
    },
    filterPillText: {
      fontSize: sf(13),
      fontWeight: '600',
      color: c.textSecondary,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    filterPillTextActive: {
      color: c.primary,
      fontWeight: '700',
    },
    listContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      marginTop: STACK_SCROLL_GUTTER,
      gap: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xl,
    },
    listContentGrow: { flexGrow: 1 },
    notificationCardWrap: {
      borderRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden',
    },
    notificationCardInner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.md,
    },
    iconSquircle: {
      width: ss(44),
      height: ss(44),
      borderRadius: ss(14),
      alignItems: 'center',
      justifyContent: 'center',
    },
    notificationBody: { flex: 1, minWidth: 0 },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.sm,
      marginBottom: ss(4),
    },
    notificationTitle: {
      flex: 1,
      fontSize: sf(15),
      fontWeight: '700',
      color: c.text,
      lineHeight: sf(20),
    },
    notificationTitleUnread: {
      fontWeight: '800',
    },
    unreadBadge: {
      width: ss(8),
      height: ss(8),
      borderRadius: ss(4),
      backgroundColor: c.primary,
      marginTop: ss(6),
      flexShrink: 0,
    },
    notificationMessage: {
      fontSize: sf(13),
      color: c.textSecondary,
      lineHeight: sf(19),
      marginBottom: ss(6),
    },
    notificationTime: {
      fontSize: sf(11),
      fontWeight: '600',
      color: c.textLight,
      letterSpacing: 0.2,
      textTransform: 'uppercase',
    },
    emptyWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.xl,
      paddingVertical: appTheme.spacing.xxl,
    },
    emptyIconWrap: {
      width: ss(88),
      height: ss(88),
      borderRadius: ss(44),
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: appTheme.spacing.lg,
      backgroundColor: c.primaryUltra ?? hexToRgba(c.primary, 0.1),
    },
    emptyTitle: {
      ...appTheme.typography.h3,
      fontSize: sf(18),
      color: c.text,
      textAlign: 'center',
      marginBottom: appTheme.spacing.sm,
    },
    emptyText: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(14),
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: sf(20),
    },
  });
}

const NotificationRow = memo(function NotificationRow({
  notification,
  index,
  defaultTitle,
  onPress,
  styles,
  colors,
  text_align,
  writing_direction,
}: {
  notification: NotificationStub;
  index: number;
  defaultTitle: string;
  onPress: () => void;
  styles: NotificationsStyles;
  colors: ThemeColors;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
}) {
  const enter_opacity = useSharedValue(0);
  const enter_translate_y = useSharedValue(18);

  useEffect(() => {
    enter_opacity.value = withTiming(1, { duration: 280 });
    enter_translate_y.value = withDelay(index * 45, withSpring(0, ENTER_SPRING));
  }, [index, enter_opacity, enter_translate_y]);

  const card_motion = useAnimatedStyle(() => ({
    opacity: enter_opacity.value,
    transform: [{ translateY: enter_translate_y.value }],
  }));

  const is_unread = !rawNotificationIsRead({ is_read: notification.is_read, read: notification.read });
  const icon_color = getIconColor(String(notification.type ?? ''), colors);
  const time_label =
    notification.date && notification.time
      ? `${notification.date} · ${notification.time}`
      : (() => {
          const created = moment(notification.created_at);
          return created.isValid() ? created.fromNow() : '';
        })();

  return (
    <Animated.View style={[card_motion]}>
      <Clickable
        onPress={onPress}
        pressScale={PRESS_PRESETS.listRow.scale}
        haptics
        accessibilityLabel={String(notification.title || defaultTitle)}
        accessibilityRole="button">
        <ContentCard
          variant="surface"
          borderless
          padding="lg"
          style={[
            styles.notificationCardWrap,
            studioCardShell(colors),
            { backgroundColor: colors.backgroundElevated },
            is_unread && { backgroundColor: colors.primaryUltra ?? hexToRgba(colors.primary, 0.08) },
          ]}>
          <View style={styles.notificationCardInner}>
            <View style={[styles.iconSquircle, { backgroundColor: hexToRgba(icon_color, 0.14) }]}>
              <SvgIcon name={getIconName(String(notification.type ?? ''))} size={22} color={icon_color} />
            </View>
            <View style={styles.notificationBody}>
              <View style={styles.titleRow}>
                <Text
                  style={[
                    styles.notificationTitle,
                    is_unread && styles.notificationTitleUnread,
                    { textAlign: text_align, writingDirection: writing_direction },
                  ]}
                  numberOfLines={2}>
                  {notification.title || defaultTitle}
                </Text>
                {is_unread ? <View style={styles.unreadBadge} /> : null}
              </View>
              <Text
                style={[styles.notificationMessage, { textAlign: text_align, writingDirection: writing_direction }]}
                numberOfLines={3}>
                {notification.content || notification.message || ''}
              </Text>
              {time_label ? (
                <Text style={[styles.notificationTime, { textAlign: text_align, writingDirection: writing_direction }]}>{time_label}</Text>
              ) : null}
            </View>
          </View>
        </ContentCard>
      </Clickable>
    </Animated.View>
  );
});

export const NotificationsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const rtl = useRtl();
  const { t } = useTranslation();
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const writing_direction: 'ltr' | 'rtl' = rtl.isRTL ? 'rtl' : 'ltr';
  const content_top_offset = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [refreshing, setRefreshing] = useState(false);
  const { data: notifications = [], unread_count: api_unread_count, isPending, error, refetch } = useNotifications();
  const { mutate: markAllAsRead } = useMarkAllAsRead();
  const { mutate: markAsRead } = useMarkAsRead();

  const theme_colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createNotificationsStyles(theme_colors), [theme_colors]);

  const is_read = useCallback((n: { is_read?: boolean; read?: boolean }) => rawNotificationIsRead({ is_read: n.is_read, read: n.read }), []);
  const is_unread = useCallback((n: { is_read?: boolean; read?: boolean }) => !is_read(n), [is_read]);

  const unread_count = api_unread_count;
  const read_count = useMemo(() => notifications.filter(is_read).length, [notifications, is_read]);

  const filtered_notifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter(is_unread);
    }
    if (filter === 'read') {
      return notifications.filter(is_read);
    }
    return notifications;
  }, [notifications, filter, is_read, is_unread]);

  const handle_mark_all_read = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  const handle_notification_press = useCallback(
    (notification: (typeof notifications)[number]) => {
      if (!rawNotificationIsRead({ is_read: notification.is_read, read: notification.read })) {
        markAsRead(notification.id);
      }
      if (notification.is_redirect) {
        (navigation as { navigate: (route: string) => void }).navigate('MySubscriptions');
      }
    },
    [markAsRead, navigation],
  );

  const filter_options = useMemo(
    (): { key: FilterKey; label: string }[] => [
      { key: 'all', label: `${t('notifications.all')} (${notifications.length})` },
      { key: 'unread', label: `${t('notifications.unread')} (${unread_count})` },
      { key: 'read', label: `${t('notifications.read')} (${read_count})` },
    ],
    [t, notifications.length, unread_count, read_count],
  );

  const right_action =
    !isPending && !error && unread_count > 0 ? (
      <HeaderIconButton
        onPress={handle_mark_all_read}
        accessibilityLabel={t('notifications.markAllRead')}
        appearance="outline"
        haptics>
        <SvgIcon name="check" size={20} color={theme_colors.primary} />
      </HeaderIconButton>
    ) : error ? (
      <HeaderIconButton onPress={() => refetch()} accessibilityLabel={t('common.refresh')} appearance="outline" haptics>
        <SvgIcon name="refresh" size={20} color={theme_colors.text} />
      </HeaderIconButton>
    ) : null;

  return (
    <StackScreenShell colors={theme_colors}>
      <ScreenHeader title={t('notifications.title')} onBack={on_back_exit} rightAction={right_action} scrollY={scrollY} />

      <View style={[styles.contentWrap, { paddingTop: content_top_offset, paddingBottom: insets.bottom }]}>
        {isPending ? (
          <LoadingState message={t('notifications.loading')} />
        ) : error ? (
          <ErrorState
            title={t('notifications.errorTitle')}
            message={(error as Error)?.message || t('notifications.errorMessage')}
            onRetry={refetch}
            error={error}
            issue_context={{
              page: t('notifications.title'),
              issue: 'Failed to load notifications',
              kind: 'api',
              api_endpoint: 'notifications',
              http_method: 'GET',
            }}
          />
        ) : (
          <>
            <View style={styles.filterRow}>
              {filter_options.map(option => {
                const active = filter === option.key;
                return (
                  <View key={option.key} style={styles.filterPillWrap}>
                    <Clickable
                      onPress={() => setFilter(option.key)}
                      accessibilityLabel={option.label}
                      accessibilityRole="button"
                      pressScale={PRESS_PRESETS.button.scale}>
                      <View style={[styles.filterPill, active && styles.filterPillActive]}>
                        <Text style={[styles.filterPillText, active && styles.filterPillTextActive]} numberOfLines={1}>
                          {option.label}
                        </Text>
                      </View>
                    </Clickable>
                  </View>
                );
              })}
            </View>

            <Animated.ScrollView
              style={{ flex: 1 }}
              keyboardShouldPersistTaps="handled"
              onScroll={onScroll}
              scrollEventThrottle={16}
              contentContainerStyle={[
                styles.listContent,
                filtered_notifications.length === 0 && styles.listContentGrow,
              ]}
              showsVerticalScrollIndicator={false}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={() => {
                    setRefreshing(true);
                    refetch().finally(() => setRefreshing(false));
                  }}
                  tintColor={theme_colors.primary}
                  colors={[theme_colors.primary]}
                  progressBackgroundColor={theme_colors.surface}
                />
              }>
              {filtered_notifications.length > 0 ? (
                filtered_notifications.map((notification, index) => (
                  <NotificationRow
                    key={String(notification.id)}
                    notification={notification}
                    index={index}
                    defaultTitle={t('notifications.defaultTitle')}
                    onPress={() => handle_notification_press(notification)}
                    styles={styles}
                    colors={theme_colors}
                    text_align={text_align}
                    writing_direction={writing_direction}
                  />
                ))
              ) : (
                <View style={styles.emptyWrap}>
                  <View style={styles.emptyIconWrap}>
                    <SvgIcon name="notifications" size={40} color={theme_colors.primary} />
                  </View>
                  <Text style={styles.emptyTitle}>{t('notifications.noNotifications')}</Text>
                  <Text style={styles.emptyText}>{t('notifications.allCaughtUp')}</Text>
                </View>
              )}
            </Animated.ScrollView>
          </>
        )}
      </View>
    </StackScreenShell>
  );
};
